import { Link } from 'react-router-dom'

const guide = {
  slug: 'does-incognito-bypass-porn-blockers',
  category: 'holds',
  kicker: 'Private browsing · VPN',
  title: 'Does incognito mode bypass a porn blocker? (And what about a VPN?)',
  seoTitle: 'Does Incognito Bypass Porn Blockers? (And VPNs?)',
  description: `Private browsing beats browser-level blockers and does nothing against DNS ones. A VPN beats DNS blockers — unless the iPhone is supervised. Layer by layer.`,
  published: '2026-08-25',
  minutes: 6,
  intro: `Two questions every blocker gets, and most answer vaguely: does
          incognito get around it, and does a VPN? The honest answer depends
          entirely on where the blocker sits. Here is the layer-by-layer
          version, so you know what you're actually buying.`,
  next: ['porn-blocker-you-cant-uninstall', 'how-to-block-porn-on-iphone', 'is-screen-time-enough-to-block-porn'],
  body: (
    <>
      <h2>What incognito actually does</h2>
      <p>
        Private browsing changes what the <b>browser remembers</b>: no
        history, no cookies kept after the window closes, no autofill. It
        doesn't change how the device reaches a website. The request still
        leaves the phone, still asks a resolver where the site is, still
        goes over the same network. Incognito is a privacy feature for the
        person who shares your device. It's not an access feature.
      </p>

      <h2>Layer 1 — Browser-level blockers: incognito wins</h2>
      <p>
        A browser extension, or a blocker that works by watching the browser,
        only sees the browser it's installed in. Open a different browser,
        open a private window where extensions are off by default, open a
        site inside an app's built-in browser — and it sees nothing. This is
        the layer most free blockers live on. Incognito walks straight
        through it.
      </p>

      <h2>Layer 2 — Screen Time: incognito is disabled, apps aren't covered</h2>
      <p>
        Apple's "Limit Adult Websites" turns off Safari's Private Browsing
        entirely while it's on, which is a good design. But the filter is a
        website filter in the browser: it doesn't apply to what other apps
        display, and it's undone by the passcode — which is yours. More in
        <Link to="/guides/is-screen-time-enough-to-block-porn"> is Screen Time enough</Link>.
      </p>

      <h2>Layer 3 — DNS-level blockers: incognito does nothing</h2>
      <p>
        A DNS-level filter answers the question every app has to ask before
        it can load anything: "where is this domain?" If the resolver
        refuses a whole category — pornography, nudity, adult themes — the
        site never resolves, in Safari, Chrome, a private window, an in-app
        browser, or an app that isn't a browser at all. Private mode changes
        nothing, because the refusal happens before the browser gets a say.
      </p>
      <p>
        This is the layer SELFCNTRL works at, on every device: iPhone, iPad,
        Mac and Windows. Incognito is simply not a variable.
      </p>

      <h2>Now the real question: what about a VPN?</h2>
      <p>
        Here's where honesty matters. A VPN routes the device's traffic
        through a tunnel, and most VPN apps <b>replace the DNS resolver</b>
        with their own when they connect. So on an ordinary device, a
        DNS-level filter — any of them, including a free filtering DNS you
        set yourself — can be sidestepped by installing a VPN app and
        turning it on. The blocker isn't broken; it's just no longer being
        asked.
      </p>
      <p>
        That's the back door most "DNS porn blockers" don't mention. It's
        also why the filter on its own isn't the product. The attachment is.
      </p>

      <h2>Layer 4 — A supervised iPhone: the VPN door is shut</h2>
      <p>
        On a supervised iPhone or iPad, the configuration profile can forbid
        adding a VPN configuration at all. The setting isn't hidden; it's
        <em> unavailable</em>. And the profile itself can't be removed from
        the phone — there's no Remove Profile button, and no passcode to
        guess because there is none. That's the combination that closes the
        two doors at once: DNS filtering for every app and every private
        window, supervision so neither the filter nor a VPN workaround can
        be changed on impulse.
      </p>
      <p>
        Taking it off is a deliberate request from your account, with a
        cooling-off delay. You stay free to stop. You're just no longer
        able to do it in thirty seconds at two in the morning. The
        mechanism in full: <Link to="/guides/porn-blocker-you-cant-uninstall">what
        makes a blocker immovable</Link>.
      </p>

      <h2>Summary</h2>
      <table>
        <thead>
          <tr><th>Blocker sits at</th><th>Incognito?</th><th>Other browser / in-app?</th><th>VPN?</th></tr>
        </thead>
        <tbody>
          <tr><td>Browser extension</td><td>Bypasses</td><td>Bypasses</td><td>Bypasses</td></tr>
          <tr><td>Screen Time</td><td>Disabled</td><td>Not covered</td><td>Passcode is yours anyway</td></tr>
          <tr><td>DNS filter (ordinary device)</td><td>No effect</td><td>Covered</td><td>Bypasses</td></tr>
          <tr><td>DNS filter, supervised iPhone</td><td>No effect</td><td>Covered</td><td>Can't be added</td></tr>
        </tbody>
      </table>

      <h2>What about Mac and Windows?</h2>
      <p>
        Apple and Microsoft don't offer supervision for a personal Mac or PC
        the way iOS does, so on a computer the DNS filter is what you get —
        with curfews and the account-level twenty-four-hour delay to loosen
        anything. In practice the phone is where 2am happens, and the phone
        is where the immovable lock goes. Device-by-device details:
        <Link to="/guides/how-to-block-porn-on-mac"> Mac</Link>,
        <Link to="/guides/how-to-block-porn-on-windows"> Windows</Link>.
      </p>
    </>
  ),
  faq: [
    {
      q: 'Does a DNS porn blocker work in incognito mode?',
      a: `Yes. Incognito changes what the browser remembers, not how the
          device resolves a domain. A category-level DNS filter refuses the
          site before any browser — private or not — gets involved.`
    },
    {
      q: 'Can a VPN bypass a porn blocker?',
      a: `On an ordinary device, a VPN can replace the DNS resolver and
          sidestep a DNS-level filter. On a supervised iPhone or iPad, the
          profile can forbid adding a VPN configuration, so the door isn't
          there. That's the difference SELFCNTRL is built on.`
    },
    {
      q: 'Does SELFCNTRL see what I browse?',
      a: `No. Filtering is by category, not by a list of what you visited;
          nothing is read, stored or reported. The resolver answers
          "blocked" or "allowed" and keeps no diary.`
    }
  ]
}

export default guide
